import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemasParams,
  Provider,
  QueryAndOutputs,
  TableSchemasList,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SINGLE_OUTPUT_NAME, SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { tempTableName } from '../utils'

const RESERVED_COLUMNS = ['h3', 'quadbin']

class DetectSpatialAnomalies extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.detectspatialanomalies',
        group: 'Statistics',
        title: 'Detect Spatial Anomalies',
        description: `
        This component can be used to detect spatial regions where the variable of interest is higher (or lower) than its baseline value.
        `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table',
          helper:
            'The source table must contain the index colum, a value column, the column representing the baseline value (called value_column_baseline) and optionally a the column representing the baseline variance (called value_column_baseline_sigma2)'
        },
        {
          name: 'indexcol',
          type: WorkflowComponentParamType.Column,
          title: 'Index column',
          parent: 'source',
          allowedColumns: RESERVED_COLUMNS,
          helper:
            'The name of the column with the unique geographic identifier of the spatial index, either "H3" or "QUADBIN"',
          dataType: [ColumnDataType.String, ColumnDataType.Number]
        },
        {
          name: 'valuecol',
          type: WorkflowComponentParamType.Column,
          title: 'Value column',
          parent: 'source',
          excludedColumns: RESERVED_COLUMNS,
          helper: 'Select the column that should be used to detect the anomalies',
          dataType: [ColumnDataType.Number]
        },
        {
          name: 'distmodel',
          type: WorkflowComponentParamType.Selection,
          title: 'Distributional Model',
          helper: 'The distributional model of the data',
          options: ['POISSON', 'GAUSSIAN']
        },
        {
          name: 'estmethod',
          type: WorkflowComponentParamType.Selection,
          title: 'Estimation Method',
          helper:
            'The estimation method used to detect the spatial anomalies. In the population-based method we expect each observed value to be proportional to its baseline under the null hypothesis, while in the expectation-based method, we expect each observed value to be equal to its baseline under the null hypothesis',
          options: ['EXPECTATION', 'POPULATION']
        },
        {
          name: 'highmeananomalies',
          type: WorkflowComponentParamType.Boolean,
          title: 'High-mean anomalies',
          default: true,
          helper: 'Option to specify if the analysis is for detecting spatial zones higher or lower than the baseline.'
        },
        {
          name: 'kringsize',
          type: WorkflowComponentParamType.Range,
          title: 'K-ring size',
          default: '',
          helper: 'The minimum and maximum k-ring size',
          optional: true,
          allowExpressions: false,
          advanced: true
        },
        {
          name: 'permutations',
          type: WorkflowComponentParamType.Number,
          title: 'Number of permutations',
          default: 99,
          helper:
            'The number of permutations used to derive the random replicas to test the anomaly statistical significance',
          optional: true,
          advanced: true
        },
        {
          name: 'maxresults',
          type: WorkflowComponentParamType.Number,
          title: 'Number of maximum results returned',
          default: 10,
          helper: 'The maximum number of spatial zones returned',
          optional: true,
          advanced: true
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const preparedParams = this._prepareParameters(params)
    const tablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const source = preparedParams.source as string
    const at = connection.quoteName(envVars.analyticsToolboxDataset as string)

    const options: {
      kring_size?: number[]
      distributional_model?: string
      estimation_method?: string
      is_high_mean_anomalies?: boolean
      permutations?: number
      max_results?: number
    } = {}

    // Check if `kringsize` is provided
    if (preparedParams.kringsize) {
      const kringsize = JSON.parse(preparedParams.kringsize as string)
      options['kring_size'] = kringsize.map((v: string) => parseFloat(v))
    }

    options.distributional_model = params.distmodel as string | undefined
    options.estimation_method = params.estmethod as string | undefined
    options.is_high_mean_anomalies = params.highmeananomalies as boolean | undefined
    options.permutations = params.permutations != null ? Math.floor(params.permutations as number) : undefined
    options.max_results = params.maxresults != null ? Math.floor(params.maxresults as number) : undefined
    const jsonSqlQuotes = `'''`

    let query = ''
    switch (connection.type) {
      case Provider.BigQuery: {
        let outTable = tablename
        let copyFromTempTable = ''
        if (codeType === WorkflowCodeType.StoredProcedure) {
          outTable = `${connection.tempLocation}.${tempTableName(componentName, preparedParams.source)}`
          copyFromTempTable = connection.getCreateTempTableQuery(
            tablename,
            `SELECT * FROM ${connection.quoteName(outTable)}`,
            codeType,
            null
          )
        }
        query = dedent`
          ${
            codeType === WorkflowCodeType.StoredProcedure
              ? `DROP TABLE IF EXISTS ${connection.quoteName(outTable)};`
              : ''
          }
          CALL ${at}.DETECT_SPATIAL_ANOMALIES(
          'SELECT * FROM ${connection.quoteName(source)}',
          '${params.indexcol as string}',
          '${params.valuecol as string}',
          '${outTable}',
          ${jsonSqlQuotes}
          ${JSON.stringify(options, null, 2)}
          ${jsonSqlQuotes}
          );
        ${copyFromTempTable}
      `
        break
      }
    }

    query = connection.wrapCodeInIfTableExistsBlock(query, [tablename], codeType)

    return {
      outputs: { result: tablename },
      query
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  _getOutputSchemas({ workflow, params, connection, componentName }: GetOutputSchemasParams): TableSchemasList {
    const fqn = this._getDryRunOutputTableFqn(workflow, params, connection, componentName, SINGLE_OUTPUT_NAME)
    const schema = [
      {
        name: 'index_scan',
        type: ColumnDataType.Number
      },
      {
        name: 'pgumbel',
        type: ColumnDataType.Number
      },
      {
        name: 'score',
        type: ColumnDataType.Number
      },
      {
        name: 'locations',
        type: ColumnDataType.String
      }
    ]

    if (params.estmethod === 'POPULATION') {
      schema.push({
        name: 'relrisk_in',
        type: ColumnDataType.Number
      })
      schema.push({
        name: 'relrisk_out',
        type: ColumnDataType.Number
      })
    } else {
      schema.push({
        name: 'relrisk',
        type: ColumnDataType.Number
      })
    }

    return [
      {
        fqn,
        schema
      }
    ]
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery]
  }
}

export { DetectSpatialAnomalies }
